/** Keeps non-empty custom field values for contact_group_list.extra_fields. */
export function normalizeExtraFields(fields: any): Record<string, any> | null {
  if (fields == null || fields === '') {
    return null;
  }

  let parsed = fields;
  if (typeof fields === 'string') {
    try {
      parsed = JSON.parse(fields);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const cleaned: Record<string, any> = {};
  Object.keys(parsed).forEach((key) => {
    const value = parsed[key];
    if (value == null || value === '') {
      return;
    }
    cleaned[key] = value;
  });

  return Object.keys(cleaned).length ? cleaned : null;
}

/** Lead columns stored alongside the custom `fields` object. */
const LEAD_COLUMN_EXTRA_FIELDS = [
  'lead_status',
  'lead_type',
  'lead_source',
  'company_name',
];

/** Custom lead `fields`, plus lead_status, lead_type, lead_source, and company_name. */
export function extraFieldsFromLead(lead: any): Record<string, any> | null {
  const merged: Record<string, any> = {
    ...(normalizeExtraFields(lead?.fields) || {}),
  };

  LEAD_COLUMN_EXTRA_FIELDS.forEach((key) => {
    const value = lead?.[key];
    if (value == null || value === '') {
      return;
    }
    merged[key] = value;
  });

  return Object.keys(merged).length ? merged : null;
}

/** Max contacts per createContactGroupList request (keeps payload under API limits). */
export const CONTACT_GROUP_LIST_BATCH_SIZE = 200;

export interface ContactGroupBatchProgress {
  added: number;
  total: number;
  batchIndex: number;
  batchCount: number;
}

export interface ContactGroupOption {
  id: number;
  name: string;
  group_type?: string;
  total_contacts?: number;
}

export function chunkArray<T>(items: T[], batchSize: number): T[][] {
  if (!items?.length || batchSize < 1) {
    return [];
  }
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    chunks.push(items.slice(i, i + batchSize));
  }
  return chunks;
}

/** Normalizes getAllContact_group API responses into a flat list for dropdowns. */
export function normalizeContactGroupListResponse(res: any): ContactGroupOption[] {
  let rows: any[] = [];
  if (Array.isArray(res)) {
    rows = res;
  } else if (Array.isArray(res?.rows)) {
    rows = res.rows;
  } else if (Array.isArray(res?.data)) {
    rows = res.data;
  } else if (res && typeof res === 'object' && res.id != null) {
    rows = [res];
  }

  return rows
    .filter((group) => group?.id != null)
    .map((group) => ({
      id: Number(group.id),
      name: String(group.name || group.group_name || `Group ${group.id}`).trim(),
      group_type: group.group_type,
      total_contacts: group.total_contacts,
    }))
    .filter((group) => group.name.length > 0);
}
